import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { exhaustMap, catchError, map, switchMap, withLatestFrom, first, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { isNil } from 'lodash-es';

import { DestinationActions, OrderActions } from '../actions';
import { OrderService } from '../order.service';
import { BookingRequest } from '../../../objects/booking-request';
import { OrderTokenManager } from '../order-token-manager';
import { BookingService } from '../booking.service';
import { LineItemService } from '../line-item.service';
import { BeachyError } from '../base';
import { CreateOrderForDeliveryDestinationPayload, CreateOrderForDestinationPayload, CreateOrderPayload } from '../actions/order.actions';
import { OwnerActionService } from '../core-action-services';
import { Store } from '@ngrx/store';
import { ApplicationState } from 'src/app/reducers';
import { Booking } from 'src/objects/booking';
import { LineItem } from 'src/objects/line-item';

import { RoutingActionService } from "src/app/core/routing.action.service";
import { getDestination } from 'src/app/reducers/destination.selectors';


// guard that checks if given item is either Booking or LineItem
// based on the fact that only Booking has property called rental_periods
function isBooking(item: Booking | LineItem): item is Booking {
  return 'rental_periods' in item;
}



@Injectable()
export class OrderEffects {
  private actions$ = inject(Actions);
  private orderService = inject(OrderService);
  private bookingService = inject(BookingService);
  private lineItemService = inject(LineItemService);
  private ownerActionService = inject(OwnerActionService);
  private store = inject<Store<ApplicationState>>(Store);
  private routingActionService = inject(RoutingActionService);

  init$ = createEffect(() => this.actions$
    .pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => {
        if (OrderTokenManager.token && OrderTokenManager.token !== 'null') {
          return OrderActions.loadOrder({ token: OrderTokenManager.token });
        }

        return OrderActions.clearOrder();
      }),
    ),
  );

  loadOrder$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.loadOrder),
      exhaustMap(({ token }) => this.orderService
        .getByToken(token)
        .pipe(
          map(order => {
            OrderTokenManager.token = order.token;

            /**
             * Make sure we load only 'new' orders with this event.
             * There's a specific event to load completed order.
             * Also, based on order's updated_at make a decision
             * if we still want to use that order, or is it going to
             * be cleared up by Lucille's worker (set to 15 minutes)
             */
            if (order.state !== 'new') {
              throw Error('Order is not new!');
            }

            return OrderActions.loadOrderSuccess({ order });
          }),
          catchError(error => of(OrderActions.loadOrderFailure({ error }))),
        ),
      ),
    ),
  );

  loadCompletedOrder$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.loadCompletedOrder),
      exhaustMap(({ orderNumber }) => this.orderService
        .getByNumber({ orderNumber })
        .pipe(
          map(completedOrder => OrderActions.loadCompletedOrderSuccess({ completedOrder })),
          catchError(error => of(OrderActions.loadCompletedOrderFailure({ error }))),
        ),
      ),
    ),
  );

  /**
   * We want to create a new order in case when destination has changed
   */
  destinationSet$ = createEffect(() => this.actions$
    .pipe(
      ofType(DestinationActions.setDestination),
      switchMap((action) => this.ownerActionService.getStorefront().pipe(first(), map(storefront => ({action, storefront})))),
      map((args) => {
        const { action: { destination }, storefront } = args;
        
        if(isNil(destination) || +(destination?.id ?? -1) <= 0) {
          return OrderActions.clearOrder();
        }
        
        let payload: CreateOrderPayload = new CreateOrderForDestinationPayload({ 
          destinationId: +destination.id,
          slug: storefront?.slug || '',
        });
        if ((destination.warehouseId ?? -1) > 0 && !!destination.deliveryAddress) {
          payload = new CreateOrderForDeliveryDestinationPayload({
            warehouseId: destination.warehouseId!,
            address: destination.deliveryAddress,
            slug: storefront?.slug || '',
          });
        }

        return OrderActions.createOrder({ payload });
      }),
    ),
  );

  createOrder$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.createOrder),
      exhaustMap(({ payload }) => this.orderService
        .create(payload)
        .pipe(
          map(order => {
            OrderTokenManager.token = order.token;

            return OrderActions.createOrderSuccess({ order });
          }),
          catchError(error => {
            OrderTokenManager.token = null;

            return of(OrderActions.createOrderFailure({ error }));
          }),
        ),
      ),
    ),
  );

  /**
   * Add items
   */
  addItem$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.addItem),
      map(payload => {
        const { request } = payload;
        if (request instanceof BookingRequest) {
          return OrderActions.addRentalItem({ request });
        }

        return OrderActions.addRetailItem({ request });
      }),
    ),
  );

  addRentalItem$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.addRentalItem),
      exhaustMap(action => {
        const { type: _, ...payload } = action;
        const token = payload.request.order.token;

        return this.bookingService
          .createBooking(payload)
          .pipe(
            exhaustMap(bookings => this.orderService
              .getByToken(token)
              .pipe(
                map(order => OrderActions.addItemSuccess({ order, lastAddedItem: (bookings ?? [])[0] })),
              ),
            ),
            catchError(error => of(OrderActions.addItemFailure({
              error: new BeachyError({
                message: 'Whoops! Looks like this product is no longer available. Please try a different product.',
                originalStackTrace: error,
              }),
            }))),
          );
      }),
    ),
  );

  addRetailItem$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.addRetailItem),
      exhaustMap(action => {
        const { type: _, ...payload } = action;
        const token = payload.request.order.token;
        return this.lineItemService
          .createLineItem(payload)
          .pipe(
            exhaustMap(lineItems => this.orderService
              .getByToken(token)
              .pipe(
                map(order => OrderActions.addItemSuccess({ order, lastAddedItem: (lineItems ?? [])[0] })),
              ),
            ),
            catchError(error => of(OrderActions.addItemFailure({
              error: new BeachyError({
                message: 'Whoops! Looks like this product is no longer available. Please try a different product.',
                originalStackTrace: error,
              }),
            }))),
          );
      }),
    ),
  );

  /**
   * Remove item
   */
  removeItem$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.removeItem),
      map(payload => {
        const { item, order } = payload;

        if (isBooking(item)) {
          return OrderActions.removeRentalItem({ item, order });
        }

        return OrderActions.removeRetailItem({ item, order });
      }),
    ),
  );

  removeRetailItem$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.removeRetailItem),
      switchMap(action => {
        const { type: _, ...payload } = action;
        return this.lineItemService
          .destroyLineItem(payload)
          .pipe(
            exhaustMap(() => this.orderService
              .getByToken(payload.order.token)
              .pipe(
                map(order => OrderActions.removeItemSuccess({ order, item: payload.item })),
              ),
            ),
            catchError(error => of(OrderActions.removeItemFailure({ error, item: payload.item }))),
          );
      }),
    ),
  );

  removeRentalItem$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.removeRentalItem),
      switchMap(action => {
        const { type: _, ...payload } = action;
        return this.bookingService
          .destroyBooking(payload)
          .pipe(
            exhaustMap(() => this.orderService
              .getByToken(payload.order.token)
              .pipe(
                map(order => OrderActions.removeItemSuccess({ order, item: payload.item })),
              ),
            ),
            catchError(error => of(OrderActions.removeItemFailure({ error, item: payload.item }))),
          );
      }),
    ),
  );

  submitPromoCode$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.submitPromoCode),
      exhaustMap(({ order, code }) => this.orderService
        .setPromotion({ order, code })
        .pipe(
          map(updatedOrder => OrderActions.submitPromoCodeSuccess({ order: updatedOrder })),
          catchError(error => {
            const status = error.status ?? -1;
            let e = error;
            if (status === 404 || status === 422) {
              e = new Error('Invalid promo code');
            }

            return of(OrderActions.submitPromoCodeFailure({ error: e }));
          }),
        ),
      ),
    ),
  );

  removePromoCode$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.removePromoCode),
      exhaustMap(({ order }) => this.orderService
        .removePromotion({ order })
        .pipe(
          map(updatedOrder => OrderActions.removePromoCodeSuccess({ order: updatedOrder })),
          catchError(error => {
            const status = error.status ?? -1;
            let e = error;
            if (status === 404 || status === 422) {
              e = new Error('Could not remove promo code');
            }

            return of(OrderActions.removePromoCodeFailure({ error: e }));
          }),
        ),
      ),
    ),
  );

  submitTip$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.submitTip),
      exhaustMap(({ order, tip }) => this.orderService
        .setTip({ order, tip })
        .pipe(
          map(updatedOrder => OrderActions.submitTipSuccess({ order: updatedOrder })),
          catchError(error => of(OrderActions.submitTipFailure({ error }))),
        ),
      ),
    ),
  );

  onAddItemSuccessRouteToMainPage$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.addItemSuccess),
      withLatestFrom(this.store.select(getDestination)),
      tap(([action, destination]) => {
        const { order } = action;
        if (destination) {
          this.routingActionService.navigateToProducts({ order, destination: destination, dateOfStay: null });
        }
      }),
    ),
    { dispatch: false },
  );
}

